<template>
  <ListPage variant="page">
    <div class="tw-min-h-[calc(100vh-80px)] tw-w-full tw-overflow-x-hidden">
      <div
        class="tw-mb-4 tw-w-full tw-border-b tw-border-white/40 tw-bg-white/35 tw-backdrop-blur-md tw-shadow-sm tw-shadow-slate-900/5 tw-py-4">
        <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1">
          <div class="tw-text-center">
            <h1 class="tw-text-2xl sm:tw-text-3xl tw-font-semibold tw-tracking-tight tw-text-slate-800">Admin</h1>
            <div class="tw-mt-1 tw-text-sm tw-text-slate-600">Company settings and user management.</div>
          </div>
        </div>
      </div>

      <div v-if="loading" key="loading-state" class="tw-flex tw-items-center tw-justify-center tw-py-16">
        <font-awesome-icon icon="circle-notch" spin class="tw-h-10 tw-w-10 tw-text-slate-400" />
      </div>

      <div v-else key="admin-content" class="tw-space-y-3">
        <div
          class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-200/50 tw-p-3 sm:tw-p-4">
          <div class="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-center md:tw-justify-between">
            <div class="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
              <div class="tw-text-base tw-font-semibold tw-text-slate-900">Company</div>
              <div class="tw-text-sm tw-text-slate-700">{{ company?.name }}</div>
            </div>

            <div class="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0 md:tw-flex-1 md:tw-px-6">
              <div class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">Company address</div>
              <div class="tw-text-sm tw-text-slate-800">{{ companyAddress || '—' }}</div>
            </div>

            <div class="tw-flex md:tw-justify-end">
              <button
                type="button"
                @click="openAddressModal"
                class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-shadow-slate-900/5 hover:tw-bg-white"
              >
                Update company address
              </button>
            </div>
          </div>
        </div>

        <Users :company="company"></Users>
      </div>
    </div>

    <Modal v-model:isOpen="isAddressModalOpen" modalId="admin-address-modal" maxWidth="lg" @close="closeAddressModal">
      <template #title>Update company address</template>
      <template #content>
        <div class="tw-space-y-4">
          <GoogleAddressInput id="admin_company_address" v-model="draftCompanyAddress" label="Company address"
            placeholder="Start typing company address..." @address-selected="handleAddressSelection" />

          <div class="tw-flex tw-justify-end tw-gap-2">
            <button type="button" class="tw-inline-flex ..." @click="closeAddressModal">Cancel</button>
            <button type="button" class="tw-inline-flex ..." @click="updateProfile">Save</button>
          </div>
        </div>
      </template>
    </Modal>
  </ListPage>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ProfileQuery from '@/gql/user/Admin.gql';
import UpdateCompanyInfo from '@/gql/company/mutations/UpdateCompanyInfo.gql';
import { pushToast } from '@/lib/toast';

import EditableInputLabel from '../core/Input/EditableInputLabel.vue';
import ListPage from '@/components/core/ListPage.vue';
import Users from './Users.vue';
import Modal from '@/components/core/Modal/Modal.vue';

import GoogleAddressInput from '../core/GoogleAddressInput.vue';

export default defineComponent({
  components: {
    EditableInputLabel,
    ListPage,
    Users,
    Modal,
    GoogleAddressInput,
  },
  data() {
    return {
      company: null as any,
      companyAddress: '',
      draftCompanyAddress: '',
      isAddressModalOpen: false,
      loading: true,
    };
  },
  methods: {
    handleAddressSelection(data: any) {
      const vm = (this as any);
      vm.draftCompanyAddress = `${data.streetAddress}, ${data.city}, ${data.state} ${data.zipCode}`;
    },
    openAddressModal() {
      const vm = this as any;
      vm.draftCompanyAddress = vm.companyAddress;
      vm.isAddressModalOpen = true;
    },
    closeAddressModal() {
      const vm = this as any;
      vm.isAddressModalOpen = false;
      vm.draftCompanyAddress = vm.companyAddress;
    },
    updateProfile() {
      const vm = (this as any);

      vm.loading = true;
      vm.$apollo
        .mutate({
          mutation: UpdateCompanyInfo,

          variables: {
            companyAddress: vm.draftCompanyAddress,
          },
        })
        .then((data) => {
          console.log(data);
          vm.loading = false;
          vm.companyAddress = vm.draftCompanyAddress;
          vm.isAddressModalOpen = false;
          pushToast('Company address updated', 'success');
        })
        .catch((error) => {
          console.error(error);
          vm.loading = false;
          pushToast('Failed to update company address', 'error');
        });
    },
  },
  apollo: {
    company: {
      query: ProfileQuery,
      result(result) {
        const vm = (this as any);

        const addr = result.data.company.address;
        if (addr) {
          (this as any).companyAddress = addr;
          (this as any).draftCompanyAddress = addr;
        }

        vm.loading = false;
      },
    },
  },
});
</script>

<style></style>
